// Keyboard shortcuts, copied from tutory/webapp's client/utils/keyBindings.js.
// Bindings are strings like 'esc', 'defmod-enter' (Cmd on macOS, else Ctrl) or 'shift-up';
// several separated by spaces. Layers registered later take precedence.
import m from './m.js'

const isMacOs = /Mac|iPhone|iPad/.test(navigator.platform)

const MODIFIERS = ['shift', 'ctrl', 'alt', 'meta']
const DEFMOD = isMacOs ? 'meta' : 'ctrl'

function range(n, fn) {
  return Object.fromEntries(Array.from({ length: n }, (_, i) => fn(i)))
}

const KEY_CODES = {
  backspace: 8,
  tab: 9,
  enter: 13,
  esc: 27,
  escape: 27,
  space: 32,
  end: 35,
  home: 36,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  del: 46,
  delete: 46,
  '+': 107,
  plus: 107,
  minus: 109,
  '=': 187,
  '-': 189,
  plusff: 171,
  minusff: 173,
  plusffenglIntl: 61,
  ...range(10, i => [`${i}`, 48 + i]),
  ...range(10, i => [`numpad${i}`, 96 + i]),
  ...range(26, i => [String.fromCharCode(97 + i), 65 + i]),
}

const keyMappings = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  Enter: 'enter',
  Backspace: 'backspace',
  Del: 'delete',
  Escape: 'escape',
}

let layers = []
export const PASS_KEYPRESS_TO_OUTER_SCOPE = Symbol('passKeypressToOuterScope')

/** Display form of the platform's command modifier, for hints: "⌘" or "Ctrl+". */
export const DEFMOD_LABEL = isMacOs ? '⌘' : 'Ctrl+'

/**
 * Punctuation and digits sit on different keys per keyboard layout, so a
 * binding of one of them without modifiers matches the character typed. With
 * modifiers it matches the key, since alt changes the character on macOS.
 */
function isCharacterKey(key) {
  return [...(key || '')].length === 1 && !/[a-z]/i.test(key)
}

function keyString(modifiers, keyId) {
  return [...MODIFIERS.filter(mod => modifiers.includes(mod)), keyId].join('-')
}

function parseKey(key) {
  const parts = key.split(/-(?!$)/).map(part => keyMappings[part] || part)
  const name = parts.pop()
  const modifiers = parts.map(part => (part === 'defmod' ? DEFMOD : part))
  const unknownModifier = modifiers.find(mod => !MODIFIERS.includes(mod))
  if (unknownModifier) throw new Error(`Unknown modifier in "${key}"`)
  if (!modifiers.length && isCharacterKey(name)) return `"${name}"`
  if (!KEY_CODES[name]) throw new Error(`Unknown key in "${key}"`)
  return keyString(modifiers, KEY_CODES[name])
}

function eventKeyStrings(evt) {
  const modifiers = MODIFIERS.filter(mod => evt[`${mod}Key`])
  const hasCommandModifier = modifiers.some(mod => mod !== 'shift')
  return [
    keyString(modifiers, evt.keyCode),
    !hasCommandModifier && isCharacterKey(evt.key) && `"${evt.key}"`,
  ].filter(Boolean)
}

function findHandlers(evt) {
  const keyStrings = eventKeyStrings(evt)
  return [...layers]
    .reverse()
    .map(({ handlers }) => keyStrings.map(key => handlers[key]).find(Boolean))
    .filter(Boolean)
}

function dispatch(evt) {
  for (const handler of findHandlers(evt)) {
    if (handler(evt) !== PASS_KEYPRESS_TO_OUTER_SCOPE) return
  }
}

function isClickable(element) {
  return (
    element.tagName === 'BUTTON' ||
    element.tagName === 'A' ||
    !!element.attributes.onclick
  )
}

function isInput(element) {
  return (
    element.tagName === 'INPUT' ||
    element.tagName === 'SELECT' ||
    element.tagName === 'TEXTAREA' ||
    !!element.attributes.contenteditable
  )
}

function createHandler(key, fn, { args, skipInInputs }) {
  return evt => {
    const activeElement = document.activeElement
    if (isClickable(activeElement) && key === parseKey('enter')) return
    if (
      isInput(activeElement) &&
      (typeof skipInInputs === 'function'
        ? skipInInputs(activeElement)
        : skipInInputs)
    )
      return
    const res = fn(...args, evt)
    if (res === PASS_KEYPRESS_TO_OUTER_SCOPE) return res
    const didSomething = res !== false
    if (didSomething) {
      evt.preventDefault()
      evt.stopPropagation()
    }
    m.redraw()
  }
}

function createHandlers(bindings, options) {
  return Object.entries(bindings).reduce((handlers, [keys, fn]) => {
    keys.split(' ').forEach(binding => {
      const key = parseKey(binding)
      handlers[key] ??= createHandler(key, fn, options)
    })
    return handlers
  }, {})
}

export function setKeyBindings(
  scope,
  bindings = {},
  { args = [], skipInInputs = true } = {}
) {
  const handlers = createHandlers(bindings, { args, skipInInputs })
  const layer = layers.find(existing => existing.scope === scope)
  if (layer) layer.handlers = { ...handlers, ...layer.handlers }
  else layers.push({ scope, handlers })
}

export function unsetKeyBindings(scope) {
  layers = layers.filter(layer => layer.scope !== scope)
}

/**
 * @param {string} scope scope of the keybindings, keep it unique for each component
 * @param {Object} bindings object with keybindings as keys and functions as values
 * @param {Object} options
 * @param {Array} options.args additional arguments to pass to the functions
 * @param {string} options.key key for m.fragment, can be empty
 * @param {boolean} options.skipInInputs should the keybindings be inactive in inputs
 */
export function keyBindingsView(
  scope,
  bindings = {},
  { args = [], key, skipInInputs = true } = {},
  children
) {
  return m.fragment(
    {
      key,
      oncreate: () => setKeyBindings(scope, bindings, { args, skipInInputs }),
      onremove: () => unsetKeyBindings(scope),
    },
    children
  )
}

/** Whether a keyboard event is exactly this binding, e.g. isKeyCombo(evt, 'defmod-enter'). */
export function isKeyCombo(evt, key) {
  return eventKeyStrings(evt).includes(parseKey(key))
}

window.addEventListener('keydown', dispatch)
